import { useEffect } from "react";
import { X, Download, Info, BarChart2, Users, TrendingUp, Activity, Clock, CheckCircle, AlertTriangle, TrendingDown, Calendar } from "react-feather";
import { Colors } from "../../../../values/colors";
import { formatDateToDisplay } from "../../../../utils/date";
import {
  ModalBackdrop, ModalContainer, ModalHeader, HeaderRow, HeaderTitle, CloseBtn, ModalBody,
  SurveyLegend, LegendItem, LegendItemHeader, LegendDot, LegendName, LegendDate,
  DownloadOutlineBtn, DownloadFilledBtn,
  Section, SectionTitle,
  AnalysisTable, AnalysisHeader, AnalysisHeaderCell, AnalysisRow, AnalysisLabel, AnalysisValue, VariationCell,
  ThermoTable, ThermoHeader, ThermoHeaderCell, ThermoRow, ThermoLabel, ThermoValue, NivelBadge,
  CatCompHeader, CatCompHeaderLabel, CatCompHeaderArrowSpacer, CategoryCard, CategoryIconWrap, CategoryName as CategoryNameStyled, CategoryValues,
  CategoryAvgsRow, CatAvg, CatArrowIcon, DeltaRow, DeltaPill, RiskLabel,
  PGRGrid, PGRCard, PGRCardLabel, PGRStats, PGRStat, PGRStatValue, PGRStatLabel,
  PGRProgress, PGRProgressLabel, PGRProgressBar, PGRProgressFill,
  OngoingSurveyAlert,
  OngoingSurveyAlertTitle,
  OngoingSurveyText,
  CardDivider,
  OngoingSurveyDate,
} from "./styles";
const CATEGORY_ICON_COLORS = [
  { bg: "rgba(93,61,237,0.1)",  icon: Colors.primaryPurple },
  { bg: "rgba(232,80,58,0.1)",  icon: "#E8503A" },
  { bg: "rgba(45,206,159,0.1)", icon: Colors.secondaryGreen },
  { bg: "rgba(136,96,224,0.1)", icon: "#8860E0" },
  { bg: "rgba(255,160,0,0.1)",  icon: Colors.orange },
  { bg: "rgba(252,97,95,0.1)",  icon: Colors.redSecondary },
  { bg: "rgba(62,209,210,0.1)", icon: Colors.defaultBlue },
];

const OLDER_COLOR = Colors.primaryPurple;
const NEWER_COLOR = Colors.orange;

function getNivel(avg) {
  if (avg >= 4) return { label: "Baixo",      bg: "rgba(45,206,159,0.12)",  color: Colors.secondaryGreen };
  if (avg >= 3) return { label: "Médio",      bg: "rgba(255,160,0,0.12)",   color: Colors.orange };
  if (avg >= 2) return { label: "Médio-Alto", bg: "rgba(250,51,51,0.10)",   color: Colors.red };
  return              { label: "Alto",        bg: "rgba(250,51,51,0.15)",   color: Colors.red };
}

function mean(arr) {
  if (!arr.length) return 0;
  return parseFloat((arr.reduce((s, v) => s + v, 0) / arr.length).toFixed(1));
}

function formatPct(val) {
  if (val === null || val === undefined) return "—";
  return `${val}%`;
}

function TriangleUp({ color }) {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" style={{ flexShrink: 0 }}>
      <path d="M5 0L10 8H0L5 0Z" fill={color} />
    </svg>
  );
}

function TriangleDown({ color }) {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" style={{ flexShrink: 0 }}>
      <path d="M5 8L0 0H10L5 8Z" fill={color} />
    </svg>
  );
}

function VariationDisplay({ newer, older, unit = "" }) {
  if (newer === null || newer === undefined || older === null || older === undefined) {
    return <VariationCell color={Colors.blueMist}>—</VariationCell>;
  }
  const delta = parseFloat((newer - older).toFixed(1));
  if (delta === 0) {
    return <VariationCell color={Colors.blueMist}>— 0</VariationCell>;
  }
  if (delta > 0) {
    return (
      <VariationCell color={Colors.secondaryGreen}>
        <TrendingUp width="16" height="16" color={Colors.secondaryGreen} />
        +{delta}{unit}
      </VariationCell>
    );
  }
  return (
    <VariationCell color={Colors.red}>
      <TrendingDown width="16" height="16" color={Colors.red} />
      {delta}{unit}
    </VariationCell>
  );
}

function CategoryDelta({ apiDelta }) {
  // apiDelta = s1_avg - s2_avg = older - newer; displayDelta = newer - older = -apiDelta
  const displayDelta = parseFloat((-apiDelta).toFixed(1));
  const isImproved = displayDelta > 0;
  const isWorsened = displayDelta < 0;
  const deltaColor  = isImproved ? Colors.secondaryGreen : isWorsened ? Colors.red : Colors.blueMist;
  const riskMeta    = isImproved
    ? { text: "Risco reduziu",  color: Colors.secondaryGreen, bg: "rgba(45,206,159,0.1)" }
    : isWorsened
    ? { text: "Risco aumentou", color: Colors.red,            bg: "rgba(250,51,51,0.08)" }
    : null;
  const absVal = Math.abs(displayDelta);

  return (
    <DeltaRow>
      <DeltaPill color={deltaColor}>
        {isImproved && <TriangleUp color={deltaColor} />}
        {isWorsened && <TriangleDown color={deltaColor} />}
        {isImproved ? `+${absVal}` : isWorsened ? `-${absVal}` : "—"}
      </DeltaPill>
      {riskMeta && (
        <RiskLabel color={riskMeta.color} bg={riskMeta.bg}>
          {riskMeta.text}
        </RiskLabel>
      )}
    </DeltaRow>
  );
}

function ResultView({ newerSurvey, olderSurvey, comparison, olderOverallAvg, newerOverallAvg, newerActionPlans, olderActionPlans, onDownloadPdf, downloadingPdf }) {
  const s2Avgs = comparison.map((c) => c.s2_avg);
  const s1Avgs = comparison.map((c) => c.s1_avg);
  const newerThermo = newerOverallAvg ?? mean(s2Avgs);
  const olderThermo = olderOverallAvg ?? mean(s1Avgs);
  const nivel = getNivel(newerThermo);

  const shortenTitle = (title, maxLen = 22) =>
    title && title.length > maxLen ? title.slice(0, maxLen) + "…" : title;

  const newerShort = shortenTitle(newerSurvey.title);
  const olderShort  = shortenTitle(olderSurvey.title);

  const hasOlderData = comparison.some((c) => c.s1_avg != null);
  const hasNewerData = comparison.some((c) => c.s2_avg != null);

  const geral = {
    category_id:   0,
    category_name: "Geral",
    s1_avg: hasOlderData ? parseFloat(olderThermo.toFixed(1)) : null,
    s2_avg: hasNewerData ? parseFloat(newerThermo.toFixed(1)) : null,
    delta:  (hasOlderData && hasNewerData) ? parseFloat((olderThermo - newerThermo).toFixed(1)) : null,
  };

  const rows = [geral, ...comparison];

  const inProressSurvey = 
  olderSurvey.status === "inProgress" 
    ? olderSurvey 
  : newerSurvey.status === "inProgress" 
    ? newerSurvey : null;

  return (
    <>
      {
        (inProressSurvey) &&
        <OngoingSurveyAlert>
          <OngoingSurveyAlertTitle>
            <AlertTriangle height={16} width={16}/>
            <span>Aviso: Pesquisa em Andamento</span>
          </OngoingSurveyAlertTitle>
          <OngoingSurveyText>
            Os dados apresentados neste documento refletem apenas as respostas computadas até a presente data, portanto, 
            os resultados são parciais e podem sofrer alterações significativas 
            até o encerramento.
          </OngoingSurveyText>
          <CardDivider />
          <OngoingSurveyDate>
            <Calendar height={12} width={12} />
            <span>
              O relatório completo e definitivo será disponibilizado no término da pesquisa em 
              <span style={{fontWeight: "bold", marginLeft: "4px"}}>
                {formatDateToDisplay(inProressSurvey.end_at, "DD/MM/YYYY")}
              </span>.
            </span>
          </OngoingSurveyDate>
        </OngoingSurveyAlert>
      }
      {/* Legend */}
      <SurveyLegend>
        <LegendItem bg="rgba(93,61,237,0.07)">
          <LegendItemHeader>
            <LegendDot color={OLDER_COLOR} />
            <LegendName color={OLDER_COLOR}>{olderSurvey.title}</LegendName>
          </LegendItemHeader>
          <LegendDate>
            {formatDateToDisplay(olderSurvey.start_at, "DD MMM YYYY")} a {formatDateToDisplay(olderSurvey.end_at, "DD MMM YYYY")}
          </LegendDate>
        </LegendItem>

        <LegendItem bg="rgba(255,160,0,0.07)">
          <LegendItemHeader>
            <LegendDot color={NEWER_COLOR} />
            <LegendName color={NEWER_COLOR}>{newerSurvey.title}</LegendName>
          </LegendItemHeader>
          <LegendDate>
            {formatDateToDisplay(newerSurvey.start_at, "DD MMM YYYY")} a {formatDateToDisplay(newerSurvey.end_at, "DD MMM YYYY")}
          </LegendDate>
        </LegendItem>
      </SurveyLegend>

      <DownloadOutlineBtn onClick={onDownloadPdf} disabled={downloadingPdf}>
        <Download size={14} />
        {downloadingPdf ? "Gerando PDF..." : "Download Relatório"}
      </DownloadOutlineBtn>

      {/* Análise Geral */}
      <Section>
        <SectionTitle>Análise Geral</SectionTitle>
        <AnalysisTable>
          <AnalysisHeader>
            <AnalysisHeaderCell />
            <AnalysisHeaderCell color={OLDER_COLOR}>{olderShort}</AnalysisHeaderCell>
            <AnalysisHeaderCell color={NEWER_COLOR}>{newerShort}</AnalysisHeaderCell>
            <AnalysisHeaderCell>Variação</AnalysisHeaderCell>
          </AnalysisHeader>

          <AnalysisRow>
            <AnalysisLabel><Users size={13} color={Colors.blueMist} /> Respondentes</AnalysisLabel>
            <AnalysisValue color={OLDER_COLOR}>{olderSurvey.n_answers}</AnalysisValue>
            <AnalysisValue color={NEWER_COLOR}>{newerSurvey.n_answers}</AnalysisValue>
            <VariationDisplay newer={newerSurvey.n_answers} older={olderSurvey.n_answers} />
          </AnalysisRow>

          <AnalysisRow>
            <AnalysisLabel><Activity size={13} color={Colors.blueMist} /> Taxa de Adesão</AnalysisLabel>
            <AnalysisValue color={OLDER_COLOR}>{formatPct(olderSurvey.percentage_completion)}</AnalysisValue>
            <AnalysisValue color={NEWER_COLOR}>{formatPct(newerSurvey.percentage_completion)}</AnalysisValue>
            <VariationDisplay newer={newerSurvey.percentage_completion} older={olderSurvey.percentage_completion} unit="%" />
          </AnalysisRow>

          <AnalysisRow>
            <AnalysisLabel><TrendingUp size={13} color={Colors.blueMist} /> Percentual</AnalysisLabel>
            <AnalysisValue color={OLDER_COLOR}>{olderThermo}</AnalysisValue>
            <AnalysisValue color={NEWER_COLOR}>{newerThermo}</AnalysisValue>
            <VariationDisplay newer={newerThermo} older={olderThermo} />
          </AnalysisRow>
        </AnalysisTable>
      </Section>

      {/* Termômetro */}
      <Section>
        <SectionTitle>Termômetro</SectionTitle>
        <ThermoTable>
          <ThermoHeader>
            <ThermoHeaderCell>Dimensão</ThermoHeaderCell>
            <ThermoHeaderCell color={OLDER_COLOR}>{olderShort}</ThermoHeaderCell>
            <ThermoHeaderCell color={NEWER_COLOR}>{newerShort}</ThermoHeaderCell>
            <ThermoHeaderCell>Variação</ThermoHeaderCell>
            <ThermoHeaderCell>Nível</ThermoHeaderCell>
          </ThermoHeader>
          <ThermoRow>
            <ThermoLabel>Termômetro</ThermoLabel>
            <ThermoValue color={OLDER_COLOR}>{olderThermo}</ThermoValue>
            <ThermoValue color={NEWER_COLOR}>{newerThermo}</ThermoValue>
            <VariationDisplay newer={newerThermo} older={olderThermo} />
            <NivelBadge bg={nivel.bg} color={nivel.color}>{nivel.label}</NivelBadge>
          </ThermoRow>
        </ThermoTable>
      </Section>

      {/* Comparação entre Categorias */}
      <Section>
        <SectionTitle>Comparação entre Categorias</SectionTitle>
        <CatCompHeader>
          <CatCompHeaderLabel color={OLDER_COLOR}>{olderShort}</CatCompHeaderLabel>
          <CatCompHeaderArrowSpacer />
          <CatCompHeaderLabel color={NEWER_COLOR}>{newerShort}</CatCompHeaderLabel>
        </CatCompHeader>

        {rows.map((cat, idx) => {
          const iconMeta = CATEGORY_ICON_COLORS[idx % CATEGORY_ICON_COLORS.length];
          return (
            <CategoryCard key={cat.category_id}>
              <CategoryIconWrap bg={iconMeta.bg}>
                <BarChart2 size={16} color={iconMeta.icon} />
              </CategoryIconWrap>
              <CategoryNameStyled>{cat.category_name}</CategoryNameStyled>
              <CategoryValues>
                <CategoryAvgsRow>
                  <CatAvg color={OLDER_COLOR}>{cat.s1_avg || 0}</CatAvg>
                  <CatArrowIcon>→</CatArrowIcon>
                  <CatAvg color={NEWER_COLOR}>{cat.s2_avg || 0}</CatAvg>
                </CategoryAvgsRow>
                <CategoryDelta apiDelta={cat.delta} />
              </CategoryValues>
            </CategoryCard>
          );
        })}
      </Section>

      {/* Evolução do Contexto de Ações (PGR) */}
      <Section>
        <SectionTitle>Evolução do Contexto de Ações (PGR)</SectionTitle>
        <PGRGrid>
          {[
            { survey: olderSurvey, plans: olderActionPlans, borderColor: OLDER_COLOR, labelColor: OLDER_COLOR },
            { survey: newerSurvey, plans: newerActionPlans, borderColor: NEWER_COLOR, labelColor: NEWER_COLOR },
          ].map(({ survey, plans, borderColor, labelColor }) => {
            const total = plans?.total ?? 0;
            const finalized = plans?.finalized ?? 0;
            const overdue = plans?.overdue ?? 0;
            const pct = Math.round((plans?.progress ?? 0) * 100);
            const progressColor = pct === 100 ? Colors.secondaryGreen : pct >= 50 ? Colors.orange : Colors.red;
            return (
              <PGRCard key={survey.id} borderColor={borderColor}>
                <PGRCardLabel color={labelColor}>{survey.title}</PGRCardLabel>
                <PGRStats>
                  <PGRStat>
                    <Clock size={16} color={Colors.blueMist} />
                    <PGRStatValue>{total}</PGRStatValue>
                    <PGRStatLabel>Total</PGRStatLabel>
                  </PGRStat>
                  <PGRStat>
                    <CheckCircle size={16} color={Colors.secondaryGreen} />
                    <PGRStatValue>{finalized}</PGRStatValue>
                    <PGRStatLabel>Concluídas</PGRStatLabel>
                  </PGRStat>
                  <PGRStat>
                    <AlertTriangle size={16} color={Colors.orange} />
                    <PGRStatValue>{overdue}</PGRStatValue>
                    <PGRStatLabel>Atrasadas</PGRStatLabel>
                  </PGRStat>
                </PGRStats>
                <PGRProgress>
                  <PGRProgressLabel>
                    <span>Progresso</span>
                    <span>{pct}%</span>
                  </PGRProgressLabel>
                  <PGRProgressBar>
                    <PGRProgressFill pct={pct} color={progressColor} />
                  </PGRProgressBar>
                </PGRProgress>
              </PGRCard>
            );
          })}
        </PGRGrid>
      </Section>

      <DownloadFilledBtn onClick={onDownloadPdf} disabled={downloadingPdf}>
        <Download size={15} />
        {downloadingPdf ? "Gerando PDF..." : "Download Relatório"}
      </DownloadFilledBtn>
    </>
  );
}

export const ComparisonModal = ({ open, surveys = [], comparison, olderOverallAvg, newerOverallAvg, actionPlansS1, actionPlansS2, onClose, onDownloadPdf, downloadingPdf }) => {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open || !comparison || surveys.length < 2) return null;

  const [surveyA, surveyB] = surveys;
  const aIsNewer = new Date(surveyA.start_at) >= new Date(surveyB.start_at);

  const newerSurvey = aIsNewer ? surveyA : surveyB;
  const olderSurvey = aIsNewer ? surveyB : surveyA;

  const olderActionPlans = aIsNewer ? actionPlansS1 : actionPlansS2;
  const newerActionPlans = aIsNewer ? actionPlansS2 : actionPlansS1;

  return (
    <ModalBackdrop onMouseDown={onClose}>
      <ModalContainer onMouseDown={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderRow>
            <HeaderTitle>
              Relatório Comparativo de Ciclos
              <Info size={14} color={Colors.blueMist} />
            </HeaderTitle>
            <CloseBtn onClick={onClose}><X size={16} /></CloseBtn>
          </HeaderRow>
        </ModalHeader>
        <ModalBody>
          <ResultView
            newerSurvey={newerSurvey}
            olderSurvey={olderSurvey}
            comparison={comparison}
            olderOverallAvg={olderOverallAvg}
            newerOverallAvg={newerOverallAvg}
            newerActionPlans={newerActionPlans}
            olderActionPlans={olderActionPlans}
            onDownloadPdf={onDownloadPdf}
            downloadingPdf={downloadingPdf}
          />
        </ModalBody>
      </ModalContainer>
    </ModalBackdrop>
  );
};
